import { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Lightbulb, Sun, FileCheck, BookOpen, CalendarCheck, ChevronRight, CheckCircle, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { def1, def2, def3, def4, def5, def6, def7, def8, def9 } from '../../assets/images';

export const getServices = (t) => [
  {
    Icon: Building2,
    title: t('services.service1.title'),
    description: t('services.service1.desc'),
    benefits: [
      t('services.service1.b1'),
      t('services.service1.b2'),
      t('services.service1.b3'),
      t('services.service1.b4')
    ]
  },
  {
    Icon: Lightbulb,
    title: t('services.service2.title'),
    description: t('services.service2.desc'),
    benefits: [
      t('services.service2.b1'),
      t('services.service2.b2'),
      t('services.service2.b3'),
      t('services.service2.b4')
    ]
  },
  {
    Icon: Sun,
    title: t('services.service3.title'),
    description: t('services.service3.desc'),
    benefits: [
      t('services.service3.b1'),
      t('services.service3.b2'),
      t('services.service3.b3'),
      t('services.service3.b4')
    ]
  },
  {
    Icon: FileCheck,
    title: t('services.service4.title'),
    description: t('services.service4.desc'),
    benefits: [
      t('services.service4.b1'),
      t('services.service4.b2'),
      t('services.service4.b3'),
      t('services.service4.b4')
    ]
  },
  {
    Icon: BookOpen,
    title: t('services.service5.title'),
    description: t('services.service5.desc'),
    benefits: [
      t('services.service5.b1'),
      t('services.service5.b2'),
      t('services.service5.b3'),
      t('services.service5.b4')
    ]
  },
  {
    Icon: CalendarCheck,
    title: t('services.service6.title'),
    description: t('services.service6.desc'),
    benefits: [
      t('services.service6.b1'),
      t('services.service6.b2'),
      t('services.service6.b3'),
      t('services.service6.b4')
    ]
  }
];

const ServiceCard = ({ service, index }) => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language && i18n.language.startsWith('en');
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="bg-white/10 backdrop-blur-md border border-white/20 card-hover group p-7 flex flex-col h-full"
    >
      {/* Icon */}
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-5 transition-all duration-500 ${
        hovered ? 'bg-auxis-gold shadow-soft-md' : 'bg-white/20'
      }`}>
        <service.Icon size={22} className={`transition-colors duration-500 ${
          hovered ? 'text-auxis-charcoal' : 'text-auxis-gold'
        }`} />
      </div>

      {/* Title */}
      <h3 className="text-subheading text-white mb-3 group-hover:text-auxis-gold transition-colors">
        {service.title}
      </h3>

      {/* Description */}
      <p className="text-body-sm text-gray-200 mb-6 flex-grow leading-relaxed">
        {service.description}
      </p>

      {/* Benefits - visible on hover via max-height transition */}
      <div className={`overflow-hidden transition-all duration-500 ease-out-expo ${
        hovered ? 'max-h-48 opacity-100 mb-5' : 'max-h-0 opacity-0 mb-0'
      }`}>
        <div className="pt-4 border-t border-white/20 space-y-2.5">
          {service.benefits.map((b, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <CheckCircle size={14} className="text-auxis-gold mt-0.5 flex-shrink-0" />
              <span className="text-body-sm text-gray-300">{b}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer link */}
      <div className="flex items-center gap-1.5 text-body-sm font-medium text-auxis-gold group-hover:gap-2.5 transition-all duration-300">
        {t('services.learnMore')}
        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />
      </div>
    </motion.div>
  );
};

const Services = () => {
  const { t } = useTranslation();
  const services = getServices(t);
  const slides = [def1, def2, def3, def4, def5, def6, def7, def8, def9];
  const [currentSlide, setCurrentSlide] = useState(1); // Start at def2

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <section id="services" className="section-padding bg-white dark:bg-auxis-charcoal relative overflow-hidden transition-colors duration-300">
      {/* Background Image Carousel */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.img 
            key={currentSlide}
            src={slides[currentSlide]} 
            alt={`AUXIS Background ${currentSlide + 1}`}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-auxis-charcoal/90 via-auxis-charcoal/70 to-auxis-charcoal/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-auxis-charcoal via-transparent to-auxis-charcoal/30" />
      </div>

      <div className="container-wide relative z-10">
        {/* Section Header */}
        <div className="max-w-2xl mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="inline-flex items-center gap-2.5 text-caption uppercase font-bold tracking-[0.15em] text-auxis-gold mb-5">
              <span className="w-2 h-2 rounded-full bg-auxis-gold" />
              {t('services.sectionSubtitle')}
            </span>
            <h2 className="text-display text-white mb-5">
              {t('services.sectionTitle')}
            </h2>
            <div className="gold-line mb-6" />
            <p className="text-body-lg text-gray-300 max-w-2xl">
              {t('services.sectionDesc')}
            </p>
          </motion.div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
